import cn from 'classnames';
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { object, string, boolean } from 'yup';
import { useFormik, FormikHelpers, FormikErrors } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import { captureException } from '@sentry/react';
import { postOrder } from '@/api';
import { submitHandler } from '@/utils/forms';
import { noop } from '@/utils';
import { AgreementContent } from '@/types/common';

import Button from '@/components/UI/Button';
import InputBool from '@/components/UI/InputBool';
import InputText from '@/components/UI/InputText';
import SROnly from '@/components/UI/SROnly';

export interface OrderFormContent {
  name: string;
  email: string;
  phone: string;
  text: string;
  message: string;
  button: string;
  agreement?: AgreementContent;
}

interface OrderFormValues {
  name: string;
  email: string;
  phone: string;
  text: string;
  agreement: boolean;
}

interface OrderFormProps {
  content: OrderFormContent;
  objectId?: string;
  source?: string;
  gaLabel?: string;
  recaptchaSiteKey?: string;
  recaptchaDisabled?: boolean;
  onSuccess?: () => void;
}

const validationSchema = object().shape({
  name: string().max(128).required(),
  email: string().email().required(),
  phone: string().max(32).required(),
  text: string().max(8192).required(),
  agreement: boolean().oneOf([true])
});

const initialValues: OrderFormValues = {
  name: '',
  email: '',
  phone: '',
  text: '',
  agreement: false
};

const TIMEOUT = 10000;

const OrderForm = ({
  content,
  objectId,
  source,
  gaLabel,
  recaptchaSiteKey,
  recaptchaDisabled,
  onSuccess: onSuccessProps = noop
}: OrderFormProps) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [message, setMessage] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSuccessMessage = useCallback(() => {
    if (content.message) {
      setMessage(content.message);

      timeoutRef.current = setTimeout(() => {
        setMessage('');
        timeoutRef.current = null;
      }, TIMEOUT);
    }
  }, [content]);

  const onSuccess = useCallback((_values: OrderFormValues, helpers: FormikHelpers<OrderFormValues>) => {
    helpers.resetForm();
    showSuccessMessage();
    onSuccessProps();
  }, [onSuccessProps, showSuccessMessage]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const {
    errors,
    isSubmitting,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit
  } = useFormik<OrderFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async(form, helper) => {
      let recaptchaToken = '';

      if (recaptchaSiteKey && !recaptchaDisabled) {
        try {
          recaptchaToken = await recaptchaRef.current!.executeAsync() ?? '';
          recaptchaRef.current!.reset();
        } catch (e) {
          captureException(e, { extra: { context: 'recaptcha', form: 'OrderForm' } });
          helper.setErrors({ nonFieldError: 'reCAPTCHA error, please try again' } as FormikErrors<OrderFormValues>);
          helper.setSubmitting(false);
          return Promise.resolve();
        }
      }

      const payload = { recaptcha_token: recaptchaToken, source, ...form };
      const onSend = () => postOrder(payload, objectId);

      return submitHandler(form, helper, onSend, onSuccess);
    }
  });

  const { nonFieldError } = errors as Record<string, string | undefined>;

  return (
    <form
      className="form order-form"
      onSubmit={handleSubmit}
    >
      <div className="form-row">
        <div className="form-col">
          <div className="form-field">
            <SROnly
              component="label"
              htmlFor="name"
            >
              {content.name}
            </SROnly>

            <InputText
              autoComplete="off"
              className={cn({ error: touched.name && errors.name })}
              id="name"
              name="name"
              placeholder={content.name}
              value={values.name}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </div>

          <div className="form-field">
            <SROnly
              component="label"
              htmlFor="email"
            >
              {content.email}
            </SROnly>

            <InputText
              autoComplete="off"
              className={cn({ error: touched.email && errors.email })}
              id="email"
              name="email"
              placeholder={content.email}
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </div>

          <div className="form-field">
            <SROnly
              component="label"
              htmlFor="phone"
            >
              {content.phone}
            </SROnly>

            <InputText
              autoComplete="off"
              className={cn({ error: touched.phone && errors.phone })}
              id="phone"
              name="phone"
              placeholder={content.phone}
              value={values.phone}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-col">
          <div className="form-field">
            <SROnly
              component="label"
              htmlFor="text"
            >
              {content.text}
            </SROnly>

            <InputText
              autoComplete="off"
              className={cn({ error: touched.text && errors.text })}
              id="text"
              name="text"
              placeholder={content.text}
              type="textarea"
              value={values.text}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-field">
        <InputBool
          className={cn({ error: touched.agreement && errors.agreement })}
          id="agreement"
          name="agreement"
          label={(
            <>
              {content.agreement?.label}
              {' '}
              <a
                href={content.agreement?.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {content.agreement?.page}
              </a>
              {' '}
              {content.agreement?.site}
            </>
          )}
          checked={values.agreement}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>

      {!!recaptchaSiteKey && !recaptchaDisabled && (
        <div className="form-field">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            size="invisible"
          />
        </div>
      )}

      {!!message && (
        <div className="form-message">
          {message}
        </div>
      )}

      {!!nonFieldError && (
        <div className="form-error">
          {nonFieldError}
        </div>
      )}

      <div className="form-button">
        <Button
          className="gold-solid"
          data-ga={gaLabel}
          disabled={isSubmitting}
          type="submit"
        >
          {content.button}
        </Button>
      </div>

      {!!recaptchaSiteKey && !recaptchaDisabled && (
        <p className="form-recaptcha-notice">
          {'This site is protected by reCAPTCHA and the Google '}
          <a
            href="https://policies.google.com/privacy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
          {' and '}
          <a
            href="https://policies.google.com/terms"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>
          {' apply.'}
        </p>
      )}
    </form>
  );
};

export default memo(OrderForm);
